import React from 'react';

const FLUID_ITEMS = ['Fuel', 'Engine Oil', 'Coolant', 'Brake Fluid', 'Power Steering', 'Wiper Fluid', 'Transmission'];
const FLUID_LEVELS = ['E', '¼', '½', '¾', 'F'];

const CONDITION_ITEMS = ['Battery', 'Tires (FL)', 'Tires (FR)', 'Tires (RL)', 'Tires (RR)', 'Spare Tire', 'Brakes', 'Wipers', 'Belts & Hoses', 'Exhaust', 'Suspension', 'Steering'];

const FUNCTIONAL_ITEMS = ['Headlights', 'Tail Lights', 'Indicators', 'Horn', 'AC / Heater', 'Radio / Audio', 'Windows', 'Locks', 'Mirrors', 'Valuables Removed', 'Keys Received', 'Damage Noted', 'Photos Taken', 'Test Drive Done', 'Customer Informed'];

const PreServiceChecklist = ({ checklist = {}, onChange }) => {
  const fluids = checklist.fluids || {};
  const conditions = checklist.conditions || {};
  const functional = checklist.functional || {};

  const updateFluids = (item, level) => {
    onChange({
      ...checklist,
      fluids: { ...fluids, [item]: level }
    });
  };

  const toggleCondition = (item) => {
    const current = conditions[item];
    // Cycle: undefined → 'ok' → 'issue' → undefined
    const next = current === 'ok' ? 'issue' : current === 'issue' ? undefined : 'ok';
    const updated = { ...conditions };
    if (next === undefined) delete updated[item];
    else updated[item] = next;
    onChange({ ...checklist, conditions: updated });
  };

  const toggleFunctional = (item) => {
    const current = functional[item];
    const next = current === 'ok' ? 'issue' : current === 'issue' ? undefined : 'ok';
    const updated = { ...functional };
    if (next === undefined) delete updated[item];
    else updated[item] = next;
    onChange({ ...checklist, functional: updated });
  };

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-medium text-gray-900">Pre-Service Checklist</h3>
      
      {/* Fluid Levels */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="font-semibold text-sm text-gray-700 mb-3 uppercase tracking-wide">Fluid Levels</h4>
        <div className="space-y-2">
          {FLUID_ITEMS.map(item => (
            <div key={item} className="flex items-center gap-2 sm:gap-3">
              <span className="w-24 sm:w-28 text-sm text-gray-700">{item}</span>
              <div className="flex gap-1">
                {FLUID_LEVELS.map(level => (
                  <button
                    key={level}
                    type="button"
                    onClick={() => updateFluids(item, level)}
                    className={`w-9 h-9 sm:w-8 sm:h-7 text-xs border rounded font-medium transition-colors ${
                      fluids[item] === level
                        ? level === 'E' ? 'bg-red-500 text-white border-red-600'
                          : level === '¼' ? 'bg-orange-400 text-white border-orange-500'
                          : level === '½' ? 'bg-yellow-400 text-gray-800 border-yellow-500'
                          : level === '¾' ? 'bg-green-300 text-gray-800 border-green-400'
                          : 'bg-green-500 text-white border-green-600'
                        : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-100'
                    }`}
                  >
                    {level}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Condition Checks */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="font-semibold text-sm text-gray-700 mb-3 uppercase tracking-wide">Condition (click to toggle: ✓ OK / ✗ Issue)</h4>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {CONDITION_ITEMS.map(item => (
            <button
              key={item}
              type="button"
              onClick={() => toggleCondition(item)}
              className={`flex items-center gap-2 px-3 py-2 rounded border text-sm text-left transition-colors ${
                conditions[item] === 'ok' ? 'bg-green-50 border-green-300 text-green-800'
                : conditions[item] === 'issue' ? 'bg-red-50 border-red-300 text-red-800'
                : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              <span className="w-5 h-5 flex items-center justify-center rounded border text-xs font-bold flex-shrink-0" style={{
                backgroundColor: conditions[item] === 'ok' ? '#22c55e' : conditions[item] === 'issue' ? '#ef4444' : '#fff',
                color: conditions[item] ? '#fff' : '#999',
                borderColor: conditions[item] === 'ok' ? '#16a34a' : conditions[item] === 'issue' ? '#dc2626' : '#d1d5db'
              }}>
                {conditions[item] === 'ok' ? '✓' : conditions[item] === 'issue' ? '✗' : ''}
              </span>
              {item}
            </button>
          ))}
        </div>
      </div>

      {/* Functional & Handover */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="font-semibold text-sm text-gray-700 mb-3 uppercase tracking-wide">Functional & Handover</h4>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {FUNCTIONAL_ITEMS.map(item => (
            <button
              key={item}
              type="button"
              onClick={() => toggleFunctional(item)}
              className={`flex items-center gap-2 px-3 py-2 rounded border text-sm text-left transition-colors ${
                functional[item] === 'ok' ? 'bg-green-50 border-green-300 text-green-800'
                : functional[item] === 'issue' ? 'bg-red-50 border-red-300 text-red-800'
                : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              <span className="w-5 h-5 flex items-center justify-center rounded border text-xs font-bold flex-shrink-0" style={{
                backgroundColor: functional[item] === 'ok' ? '#22c55e' : functional[item] === 'issue' ? '#ef4444' : '#fff',
                color: functional[item] ? '#fff' : '#999',
                borderColor: functional[item] === 'ok' ? '#16a34a' : functional[item] === 'issue' ? '#dc2626' : '#d1d5db'
              }}>
                {functional[item] === 'ok' ? '✓' : functional[item] === 'issue' ? '✗' : ''}
              </span>
              {item}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PreServiceChecklist;
